/**
 * Die Wörter des Ein-Klick-Updates der Mac-App („Jetzt aktualisieren“ oben in Vespera). (12.10.2026, Bahn server-update-starten)
 *
 * EINE Liste für Server und Oberfläche. Vom Mac kommt NIE ein freier Text in den Server, in die Oberfläche oder ins Modell:
 * nur ein Wort aus dieser Liste. Was nicht darauf steht, wird verworfen (server/update-ablauf.ts liesZustand).
 *
 * ## Vertrag (Plan docs/plan-desktop-1-3-0-2026-10-11.md, A7; Beschreibung in docs/update-starten-2026-10-12.md)
 *
 *   Server → App   {"type":"update_starten","req_id":"<Kennung>"}        nur, wenn `kann` das Wort "update_starten" trägt
 *   App → Server   {"type":"update_starten_result","req_id","zustand","fehler"?,"version"?}
 *                  die weiteren Zwischenstände mit DERSELBEN req_id (auch unter dem Typ "update_zustand", so sendet es der
 *                  Zweig desktop-touchid heute); ein Fehler darf auch als "fehler:<wort>" im Feld zustand stehen.
 *
 * Nie eine Installation ohne Touch ID am Mac: der Server startet nur den Ablauf der App; installiert wird erst nach dem Finger.
 */
import { istVersion } from "./app-version.js";

/** Das Wort in `kann` (session_start), mit dem die Mac-App sagt, dass sie `update_starten` versteht. */
export const KANN_UPDATE_STARTEN = "update_starten";

/** Die Adressen der Weboberfläche (server/update-starten-route.ts) und der Sonderkopf, den nur die eigene Seite setzt (CSRF). */
export const UPDATE_PFAD_STARTEN = "/api/daemon/update/starten";
export const UPDATE_PFAD_ZUSTAND = "/api/daemon/update/zustand";
export const UPDATE_KOPF = "x-vespera-aktion";
export const UPDATE_KOPF_WERT = "update-starten";

/** Was die App als Zustand melden darf (und sonst nichts). */
export const UPDATE_APP_ZUSTAENDE = [
  "gestartet",
  "laedt",
  "geladen",
  "wartet-auf-touchid",
  "installiert",
  "abgelehnt",
  "abgebrochen",
  "zeitueberschreitung",
  "nicht-verfuegbar",
  "kein-update",
  "fehler",
  "laeuft-schon",
  "zu-oft",
] as const;
export type UpdateAppZustand = (typeof UPDATE_APP_ZUSTAENDE)[number];

/** Was nur der Server sagt: der Start wurde nicht einmal versucht, oder die Antwort blieb aus. */
export const UPDATE_SERVER_WOERTER = ["nicht-erlaubt", "app-zu-alt", "kein-mac", "geraet-unklar", "keine-antwort", "geraet-getrennt"] as const;
type UpdateServerWort = (typeof UPDATE_SERVER_WOERTER)[number];

export type UpdateWort = UpdateAppZustand | UpdateServerWort;

/** Das Ablauf läuft noch: es kommt noch etwas von der App. */
const UPDATE_LAUFEND: ReadonlySet<string> = new Set(["gestartet", "laedt", "geladen", "wartet-auf-touchid"]);
export const updateLaeuft = (z: unknown): boolean => typeof z === "string" && UPDATE_LAUFEND.has(z);

/**
 * Wartet der Server noch auf ein Wort des Macs? Die vier laufenden Wörter UND `geraet-getrennt`: reißt die Leitung beim Laden kurz ab,
 * fragt der Mac womöglich weiter nach Touch ID und meldet sich mit demselben Gerät und derselben Kennung wieder (12.10.2026, Prüfung Opus).
 * Dann zählt sein späteres Wort, bis die Frist des Servers um ist. (`updateLaeuft` bleibt strenger: nur die vier — Knopf und neuer Start.)
 */
export const updateErwartetNoch = (z: unknown): boolean => updateLaeuft(z) || z === "geraet-getrennt";

/** Die Gründe, aus denen ein `fehler` kommt — die Codes der Mac-App (Zweig desktop-touchid) plus die des Servers. */
const FEHLER_GRUPPEN = {
  netz: ["offline", "tls", "server-fehler", "weiterleitung", "adresse", "antwort-falsch", "antwort", "nicht-gefunden", "server"],
  pruefung: [
    "pruefsumme-falsch",
    "groesse-falsch",
    "signatur",
    "signatur-anders",
    "signatur-anforderung",
    "signatur-laufend",
    "ohne-unterschrift",
    "von-hand-ohne-unterschrift",
    "unterschrift",
    "nicht-beglaubigt",
    "kennung",
    "version-falsch",
    "nicht-neuer",
    "inhalt",
    "paket-verlinkt",
    "kein-paket",
    "angebot",
  ],
  platz: ["kein-platz"],
  schluessel: ["kein-schluessel", "schluessel-abgelehnt", "konto-ohne-recht"],
  besetzt: ["pausiert", "schon-aktiv", "laeuft-schon", "zu-viele", "zu-viele-starts"],
  mac: [
    "nicht-moeglich",
    "unerwartet",
    "einhaengen",
    "kopieren",
    "ordner",
    "freigabe",
    "skript-gescheitert",
    "start",
    "abgebrochen",
    "touchid-abgelehnt",
  ],
} as const;
type FehlerGruppe = keyof typeof FEHLER_GRUPPEN;

const FEHLER_NACH_GRUPPE: ReadonlyMap<string, FehlerGruppe> = new Map(
  (Object.entries(FEHLER_GRUPPEN) as [FehlerGruppe, readonly string[]][]).flatMap(([gruppe, woerter]) =>
    woerter.map((w): [string, FehlerGruppe] => [w, gruppe]),
  ),
);

/** Alle Fehlerwörter, die der Server durchlässt. Alles andere wird zu „unbekannt“. */
export const UPDATE_FEHLER_WOERTER: readonly string[] = [...FEHLER_NACH_GRUPPE.keys(), "unbekannt"];

/** Das Fehlerwort aus der Liste — oder „unbekannt“. Nie der Text der App. */
export function updateFehlerWort(roh: unknown): string {
  return typeof roh === "string" && FEHLER_NACH_GRUPPE.has(roh) ? roh : "unbekannt";
}

/** Eine Version aus der Nachricht der App, wenn sie wie eine aussieht (Ziffern und Punkte) — sonst null. */
export function updateVersion(roh: unknown): string | null {
  return istVersion(roh) ? roh : null;
}

const SAETZE: Readonly<Record<UpdateWort, string>> = {
  gestartet: "Lade … Gleich fragt der Mac nach Touch ID.",
  laedt: "Lade … Gleich fragt der Mac nach Touch ID.",
  geladen: "Geladen und geprüft. Bitte am Mac mit Touch ID bestätigen.",
  "wartet-auf-touchid": "Bitte am Mac mit Touch ID bestätigen. Ohne Finger wird nichts installiert.",
  installiert: "Installiert. Vespera Desktop startet neu und meldet sich gleich wieder.",
  abgelehnt: "Abgelehnt – es wurde nichts installiert.",
  abgebrochen: "Abgebrochen – es wurde nichts installiert.",
  zeitueberschreitung: "Die Touch-ID-Frage am Mac ist abgelaufen – es wurde nichts installiert.",
  "nicht-verfuegbar": "Touch ID ist an diesem Mac nicht verfügbar – es wurde nichts installiert.",
  "kein-update": "Es gibt nichts Neueres zu installieren.",
  fehler: "Das Aktualisieren hat am Mac nicht geklappt.",
  "laeuft-schon": "Am Mac läuft schon eine Aktualisierung. Bitte dort mit Touch ID bestätigen oder abbrechen.",
  "zu-oft": "Zu oft versucht. Bitte in einer Stunde noch einmal.",
  "nicht-erlaubt": "Das darf nur das Betreiberkonto.",
  "app-zu-alt": "Diese App kann das noch nicht: bitte einmal von Hand installieren.",
  "kein-mac": "Es ist gerade kein Mac verbunden.",
  "geraet-unklar": "Mehrere Macs sind verbunden. Bitte am Mac in der Liste aktualisieren, den du meinst.",
  "keine-antwort": "Der Mac hat nicht geantwortet. Hier wurde nichts installiert – bitte am Mac nachsehen.",
  "geraet-getrennt": "Die Verbindung riss. Wenn der Mac nach Touch ID fragt, bestätige dort.",
};

const FEHLER_SAETZE: Readonly<Record<FehlerGruppe | "unbekannt", string>> = {
  netz: "Der Mac konnte die neue Fassung nicht holen. Es wurde nichts installiert.",
  pruefung: "Die geladene Fassung hat die Prüfung nicht bestanden. Es wurde nichts installiert.",
  platz: "Auf dem Mac ist zu wenig Platz. Es wurde nichts installiert.",
  schluessel: "Die Kopplung des Macs reicht dafür nicht. Bitte den Mac neu koppeln oder einmal von Hand installieren.",
  besetzt: "Der Mac ist gerade mit etwas anderem beschäftigt oder pausiert. Bitte später noch einmal.",
  mac: "Das Aktualisieren am Mac ist gescheitert. Bitte am Mac nachsehen.",
  unbekannt: "Das Aktualisieren am Mac ist gescheitert. Bitte am Mac nachsehen.",
};

/** Der Satz in einfachen Worten für ein Wort (und bei `fehler` für das Fehlerwort). */
export function updateSatz(wort: UpdateWort, fehler?: string | null): string {
  if (wort === "fehler") return FEHLER_SAETZE[FEHLER_NACH_GRUPPE.get(fehler ?? "") ?? "unbekannt"];
  return SAETZE[wort];
}

/** Wie ein Wort aussieht: ruhig laufend, braucht den Finger, fertig, gescheitert oder nur ein Hinweis (nichts ist passiert). */
export type UpdateArt = "laeuft" | "achtung" | "ok" | "fehler" | "hinweis";

export function updateArt(wort: UpdateWort): UpdateArt {
  switch (wort) {
    case "gestartet":
    case "laedt":
    case "geladen":
      return "laeuft";
    case "wartet-auf-touchid":
    case "geraet-getrennt":
      return "achtung";
    case "installiert":
      return "ok";
    case "fehler":
    case "keine-antwort":
      return "fehler";
    default:
      return "hinweis";
  }
}

/** Ist das ein Wort, das der Server in der Antwort an die Oberfläche setzen darf? */
export function istUpdateWort(x: unknown): x is UpdateWort {
  return typeof x === "string" && (UPDATE_APP_ZUSTAENDE.includes(x as UpdateAppZustand) || UPDATE_SERVER_WOERTER.includes(x as UpdateServerWort));
}

/** Der Stand eines Ablaufs, wie ihn Server (GET /api/daemon/update/zustand, /api/daemon-status) und Oberfläche austauschen. */
export interface UpdateStandAntwort {
  /** Darf dieses Konto das überhaupt (nur das Betreiberkonto)? */
  erlaubt: boolean;
  /** Das letzte Wort des Ablaufs — null, wenn noch keiner lief (seit dem Serverstart). */
  zustand: UpdateWort | null;
  /** Bei `fehler`: das Fehlerwort aus der Liste. */
  fehler: string | null;
  /** Die geprüfte Version der neuen Fassung, sobald der Mac sie meldet. */
  version: string | null;
  /** Wartet der Server noch auf den Mac? */
  laeuft: boolean;
  /** Wann das Wort kam (ISO). */
  um: string | null;
  /** Auf welchem Mac der Ablauf läuft (Gerätekennung). */
  geraet: string | null;
}
